/**
 * Filter Data Utility for R4S OmniMens
 * Client-side data filtering with support for various field types and operators
 */

import {
  FilterCondition,
  FilterGroup,
  WidgetFilterConfig,
  FilterFieldType,
} from '../types/filters';

/**
 * Apply filters to a dataset
 */
export function applyFilters(
  data: Record<string, unknown>[],
  filterConfig: WidgetFilterConfig
): Record<string, unknown>[] {
  if (!filterConfig.enabled || filterConfig.groups.length === 0) {
    return data;
  }

  return data.filter((row) => evaluateFilterGroups(row, filterConfig.groups));
}

/**
 * Evaluate multiple filter groups (groups are AND-ed together)
 */
function evaluateFilterGroups(
  row: Record<string, unknown>,
  groups: FilterGroup[]
): boolean {
  if (groups.length === 0) return true;

  // All groups must pass (AND logic between groups)
  return groups.every((group) => evaluateFilterGroup(row, group));
}

/**
 * Evaluate a single filter group
 */
function evaluateFilterGroup(
  row: Record<string, unknown>,
  group: FilterGroup
): boolean {
  if (group.conditions.length === 0) return true;

  const results = group.conditions.map((condition) =>
    evaluateCondition(row, condition)
  );

  if (group.logic === 'and') {
    return results.every((result) => result);
  } else {
    return results.some((result) => result);
  }
}

/**
 * Evaluate a single filter condition
 */
function evaluateCondition(
  row: Record<string, unknown>,
  condition: FilterCondition
): boolean {
  const { field, fieldType, operator, value, value2 } = condition;

  // Get the field value from the row
  const fieldValue = getNestedValue(row, field);

  // Handle empty/null checks first
  if (operator === 'is_empty') {
    return fieldValue === null || fieldValue === undefined || fieldValue === '';
  }
  if (operator === 'is_not_empty') {
    return fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
  }

  // Handle boolean operators
  if (operator === 'is_true') {
    return fieldValue === true || fieldValue === 'Yes' || fieldValue === 'true' || fieldValue === 1;
  }
  if (operator === 'is_false') {
    return fieldValue === false || fieldValue === 'No' || fieldValue === 'false' || fieldValue === 0;
  }

  // Handle date-specific operators
  if (fieldType === 'date') {
    return evaluateDateCondition(fieldValue, operator, value, value2);
  }

  // Handle number-specific operators
  if (fieldType === 'number') {
    return evaluateNumberCondition(fieldValue, operator, value, value2);
  }

  // Handle string operators (default)
  return evaluateStringCondition(fieldValue, operator, value);
}

/**
 * Get nested value from object using dot notation
 */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce((current: unknown, key) => {
    if (current && typeof current === 'object' && key in (current as object)) {
      return (current as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

/**
 * Evaluate string condition
 */
function evaluateStringCondition(
  fieldValue: unknown,
  operator: string,
  filterValue: unknown
): boolean {
  const strFieldValue = String(fieldValue ?? '').toLowerCase();
  const strFilterValue = String(filterValue ?? '').toLowerCase();

  switch (operator) {
    case 'equals':
      return strFieldValue === strFilterValue;
    case 'not_equals':
      return strFieldValue !== strFilterValue;
    case 'contains':
      return strFieldValue.includes(strFilterValue);
    case 'starts_with':
      return strFieldValue.startsWith(strFilterValue);
    case 'ends_with':
      return strFieldValue.endsWith(strFilterValue);
    default:
      return true;
  }
}

/**
 * Evaluate number condition
 */
function evaluateNumberCondition(
  fieldValue: unknown,
  operator: string,
  filterValue: unknown,
  filterValue2?: unknown
): boolean {
  const numFieldValue = parseFloat(String(fieldValue));
  const numFilterValue = parseFloat(String(filterValue));
  const numFilterValue2 = filterValue2 !== undefined ? parseFloat(String(filterValue2)) : undefined;

  if (isNaN(numFieldValue)) return false;
  if (isNaN(numFilterValue) && operator !== 'is_empty' && operator !== 'is_not_empty') return false;

  switch (operator) {
    case 'equals':
      return numFieldValue === numFilterValue;
    case 'not_equals':
      return numFieldValue !== numFilterValue;
    case 'greater_than':
      return numFieldValue > numFilterValue;
    case 'less_than':
      return numFieldValue < numFilterValue;
    case 'greater_or_equal':
      return numFieldValue >= numFilterValue;
    case 'less_or_equal':
      return numFieldValue <= numFilterValue;
    case 'between':
      if (numFilterValue2 === undefined || isNaN(numFilterValue2)) return false;
      return numFieldValue >= numFilterValue && numFieldValue <= numFilterValue2;
    default:
      return true;
  }
}

/**
 * Evaluate date condition
 */
function evaluateDateCondition(
  fieldValue: unknown,
  operator: string,
  filterValue: unknown,
  filterValue2?: unknown
): boolean {
  // Parse the field value as date
  const fieldDate = parseDate(fieldValue);
  if (!fieldDate) return false;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (operator) {
    case 'equals': {
      const filterDate = parseDate(filterValue);
      if (!filterDate) return false;
      return isSameDay(fieldDate, filterDate);
    }
    case 'before': {
      const filterDate = parseDate(filterValue);
      if (!filterDate) return false;
      return fieldDate < filterDate;
    }
    case 'after': {
      const filterDate = parseDate(filterValue);
      if (!filterDate) return false;
      return fieldDate > filterDate;
    }
    case 'between': {
      const startDate = parseDate(filterValue);
      const endDate = parseDate(filterValue2);
      if (!startDate || !endDate) return false;
      return fieldDate >= startDate && fieldDate <= endDate;
    }
    case 'last_n_days': {
      const days = parseInt(String(filterValue), 10);
      if (isNaN(days)) return false;
      const cutoffDate = new Date(today);
      cutoffDate.setDate(cutoffDate.getDate() - days);
      return fieldDate >= cutoffDate;
    }
    case 'today': {
      return isSameDay(fieldDate, today);
    }
    case 'this_week': {
      const weekStart = new Date(today);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay()); // Start of week (Sunday)
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6); // End of week (Saturday)
      return fieldDate >= weekStart && fieldDate <= weekEnd;
    }
    case 'this_month': {
      return fieldDate.getMonth() === now.getMonth() &&
             fieldDate.getFullYear() === now.getFullYear();
    }
    default:
      return true;
  }
}

/**
 * Parse various date formats
 */
function parseDate(value: unknown): Date | null {
  if (!value) return null;

  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'string') {
    // Handle ISO format (2024-12-20T13:47:04Z)
    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:T|$)/);
    if (isoMatch) {
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date;
    }

    // Handle German format (20.12.2024)
    const deMatch = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (deMatch) {
      const [, day, month, year] = deMatch;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
  }

  if (typeof value === 'number') {
    // Assume timestamp
    return new Date(value);
  }

  return null;
}

/**
 * Check if two dates are the same day
 */
function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * Detect field type from sample data
 */
export function detectFieldTypeFromData(
  data: Record<string, unknown>[],
  fieldName: string
): FilterFieldType {
  const samples = data
    .slice(0, 10)
    .map((row) => getNestedValue(row, fieldName))
    .filter((v) => v !== null && v !== undefined && v !== '');

  if (samples.length === 0) return 'string';

  const sample = samples[0];

  if (typeof sample === 'boolean') return 'boolean';
  if (typeof sample === 'number') return 'number';
  if (typeof sample === 'string') {
    // Check if it looks like a date
    if (/^\d{4}-\d{2}-\d{2}/.test(sample)) return 'date';
    if (/^\d{2}\.\d{2}\.\d{4}/.test(sample)) return 'date';
    // Check if it's a number string
    if (!isNaN(Number(sample)) && sample.trim() !== '') return 'number';
  }

  return 'string';
}

/**
 * Get unique values for a field (for filter suggestions)
 */
export function getUniqueValues(
  data: Record<string, unknown>[],
  fieldName: string,
  limit: number = 50
): string[] {
  const values = new Set<string>();

  for (const row of data) {
    if (values.size >= limit) break;
    const value = getNestedValue(row, fieldName);
    if (value !== null && value !== undefined && value !== '') {
      values.add(String(value));
    }
  }

  return Array.from(values).sort();
}

/**
 * Count results matching a filter
 */
export function countFilteredResults(
  data: Record<string, unknown>[],
  filterConfig: WidgetFilterConfig
): number {
  return applyFilters(data, filterConfig).length;
}

/**
 * Convert filter config to OData $filter string (for backend filtering)
 */
export function filterConfigToOData(filterConfig: WidgetFilterConfig): string | null {
  if (!filterConfig.enabled || filterConfig.groups.length === 0) {
    return null;
  }

  const groupStrings = filterConfig.groups
    .map((group) => filterGroupToOData(group))
    .filter((s) => s !== null);

  if (groupStrings.length === 0) return null;

  return groupStrings.join(' and ');
}

/**
 * Convert a filter group to OData string
 */
function filterGroupToOData(group: FilterGroup): string | null {
  const conditionStrings = group.conditions
    .map((condition) => conditionToOData(condition))
    .filter((s) => s !== null);

  if (conditionStrings.length === 0) return null;

  const joined = conditionStrings.join(group.logic === 'and' ? ' and ' : ' or ');
  return conditionStrings.length > 1 ? `(${joined})` : joined;
}

/**
 * Convert a condition to OData string
 */
function conditionToOData(condition: FilterCondition): string | null {
  const { field, operator, value, value2 } = condition;

  if (!field) return null;

  switch (operator) {
    case 'equals':
      return typeof value === 'string'
        ? `${field} eq '${escapeOData(value)}'`
        : `${field} eq ${value}`;
    case 'not_equals':
      return typeof value === 'string'
        ? `${field} ne '${escapeOData(value)}'`
        : `${field} ne ${value}`;
    case 'contains':
      return `contains(${field}, '${escapeOData(String(value))}')`;
    case 'starts_with':
      return `startswith(${field}, '${escapeOData(String(value))}')`;
    case 'ends_with':
      return `endswith(${field}, '${escapeOData(String(value))}')`;
    case 'greater_than':
      return `${field} gt ${value}`;
    case 'less_than':
      return `${field} lt ${value}`;
    case 'greater_or_equal':
      return `${field} ge ${value}`;
    case 'less_or_equal':
      return `${field} le ${value}`;
    case 'between':
      return `${field} ge ${value} and ${field} le ${value2}`;
    case 'is_empty':
      return `${field} eq null`;
    case 'is_not_empty':
      return `${field} ne null`;
    default:
      return null;
  }
}

/**
 * Escape special characters for OData
 */
function escapeOData(value: string): string {
  return value.replace(/'/g, "''");
}
